import { ReactNode } from "react";
import { BannerFuentes } from "@/components/BannerFuentes";

// === FUENTE PRIMARIA: Informe de Control 8536-2026-CG/JUSPE-SCE (22-may-2026)
// Tomo I de XIV · 130 páginas · texto verificado por OCR + lectura directa.
// Cifras de competencia, prestaciones, oferta económica: todas extraídas del PDF CGR.
// Eventos pre-22-may (allanamiento, denuncias) siguen siendo del SPEC — marcados.

const EXPEDIENTE = {
  empresa: "Servicios Generales Gálaga S.A.C.",
  representante: "Juan Charles Alvarado Pfuyo",
  contratoPrincipal: {
    numero: "n.° 175-2026-ONPE",
    monto: 6_368_332.75,
    objeto:
      "Servicio de transporte de carga a nivel nacional — despliegue y repliegue de material, equipos informáticos electorales e implementos para simulacro y sufragio — EG 2026",
    fechaContrato: "2026-03-19",
  },
  prestacionesAdicionales: {
    monto: 650_205.84,
    incrementoPrecioRepliegue: "600% (de S/ 0.50/kg a S/ 6.00/kg para repliegue local-Lurín)",
    aprobadoPor: "Resolución Gerencial n.° 000192-2026-GAD/ONPE (9-abr-2026)",
    cgrHalla: "Sin sustento técnico · servicio ya estaba contratado · beneficio directo al contratista",
  },
  ofertasCompetidoras: [
    { empresa: "Hermes Transportes Blindados S.A.", monto: 6_200_184.55, deltaConGalaga: -168_148.19 },
    { empresa: "Senlas S.A.", monto: 5_898_928.66, deltaConGalaga: -469_404.09 },
  ],
  vehiculosUsados: { propios: 46, terceros: 437, total: 483, pctTerceros: 92 },
  alertaPreviaArchivada: {
    fecha: "2026-02-11",
    remitente: "Consorcio AFE",
    destinatario: "Piero Corvetto Salinas (Jefe ONPE)",
    accion: "Archivada sin investigación interna",
    fuente: "Prensa — pendiente verificar contra expediente fiscal",
  },
};

type Investigado = {
  nombre: string;
  dni: string;
  cargo: string;
  periodo: string;
  hechos: string[];
  fuente: "CGR" | "SPEC_prensa";
};

// CGR Informe 8536-2026 · Capítulo IV "Identificación de personas involucradas"
const INVESTIGADOS_CGR: Investigado[] = [
  {
    nombre: "Piero Alessandro Corvetto Salinas",
    dni: "07381352",
    cargo: "Jefe de la ONPE",
    periodo: "31-ago-2020 al 21-abr-2026",
    hechos: ["Omitió garantizar el cumplimiento de objetivos y metas institucionales del proceso electoral 2026 (irregularidad 2)"],
    fuente: "CGR",
  },
  {
    nombre: "José Edilberto Samamé Blas",
    dni: "10139529",
    cargo: "Gerente de Gestión Electoral",
    periodo: "1-dic-2023 al 12-abr-2026",
    hechos: ["Tramitó los términos de referencia sin determinar la cuantificación real de vehículos requeridos", "Permitió el traslado de equipos informáticos para capacitación sin asegurar devolución oportuna"],
    fuente: "CGR",
  },
  {
    nombre: "Juan Antonio Phang Sánchez",
    dni: "21579416",
    cargo: "Subgerente de Producción Electoral",
    periodo: "4-mar-2024 al 7-abr-2026",
    hechos: ["Elaboró y suscribió términos de referencia sin determinar cuantificación real de vehículos"],
    fuente: "CGR",
  },
  {
    nombre: "Yuwitza Milagros Aguilar Ticona",
    dni: "41247790",
    cargo: "Subgerente de Logística",
    periodo: "25-jun-2019 a la fecha",
    hechos: ["Tramitación de prestaciones adicionales sin sustento técnico"],
    fuente: "CGR",
  },
  {
    nombre: "Edward Eloy Alarcón González",
    dni: "07497613",
    cargo: "Gerente de Administración",
    periodo: "10-oct-2025 a la fecha",
    hechos: ["Aprobó prestaciones adicionales por S/ 650,205.84 sin sustento técnico mediante Resolución Gerencial n.° 000192-2026-GAD/ONPE de 9-abr-2026"],
    fuente: "CGR",
  },
  {
    nombre: "Juan Enrique Pestana Uriba",
    dni: "15942504",
    cargo: "Gerente de Asesoría Jurídica",
    periodo: "16-oct-2023 a la fecha",
    hechos: ["Emisión de informes legales que viabilizaron las prestaciones adicionales"],
    fuente: "CGR",
  },
  {
    nombre: "William Martín García Velásquez",
    dni: "06735125",
    cargo: "Asistente",
    periodo: "(detalle en Apéndice 1 del informe)",
    hechos: ["Participación en evaluación de oferta — datos inexactos sobre capacidad de carga de vehículos del postor adjudicado"],
    fuente: "CGR",
  },
  {
    nombre: "Lilia Flores Bancho",
    dni: "07229719",
    cargo: "Contratada para el servicio",
    periodo: "(detalle en Apéndice 1)",
    hechos: ["Participación en evaluación de oferta"],
    fuente: "CGR",
  },
];

const INVESTIGADOS_OTROS: Investigado[] = [
  {
    nombre: "Juan Charles Alvarado Pfuyo",
    dni: "(no aparece en CGR · expediente fiscal aparte)",
    cargo: "Dueño de Servicios Generales Gálaga S.A.C.",
    periodo: "—",
    hechos: ["Investigado por Fiscalía Anticorrupción", "12 allanamientos simultáneos 24-abr-2026"],
    fuente: "SPEC_prensa",
  },
];

type Hito = { fecha: string; evento: string; cat: "probado" | "contradiccion" | "hipotesis"; emoji: string; fuente?: string };

const TIMELINE: Hito[] = [
  { fecha: "2026-02-11", emoji: "🟠", cat: "hipotesis", evento: "Carta AFE alertando corrupción enviada a Corvetto · archivada sin investigación", fuente: "Prensa (pendiente verificar)" },
  { fecha: "2026-03-19", emoji: "🟢", cat: "probado", evento: "Contrato n.° 175-2026-ONPE adjudicado a Gálaga por S/ 6,368,332.75 (oferta superior en S/ 469,404.09 a Senlas y S/ 168,148.19 a Hermes Transportes Blindados)", fuente: "CGR 8536-2026" },
  { fecha: "2026-04-05", emoji: "🟢", cat: "probado", evento: "Repliegue de equipos de capacitación culmina entre 5-7 abr (POE establecía 4-abr) · genera desfase logístico", fuente: "CGR 8536-2026" },
  { fecha: "2026-04-09", emoji: "🟢", cat: "probado", evento: "Resolución Gerencial 000192-2026-GAD/ONPE aprueba prestaciones adicionales por S/ 650,205.84 con incremento 600% precio repliegue", fuente: "CGR 8536-2026" },
  { fecha: "2026-04-11", emoji: "🔴", cat: "contradiccion", evento: "A las 15:28 h se reprograman 43 rutas con 353 locales · 3,261 mesas · 2,453,479 electores · entregadas recién 12-13 abr (en lugar del 11-abr POE)", fuente: "CGR 8536-2026" },
  { fecha: "2026-04-12", emoji: "🟢", cat: "probado", evento: "Día de elección: 256 locales reciben material con retraso · 3,605 mesas afectadas · 1,071,111 electores en universo · 187 mesas no instaladas en 13 locales Lima Metropolitana · 55,261 electores hábiles no votaron", fuente: "CGR 8536-2026 · JNE Acuerdo Pleno 23-abr" },
  { fecha: "2026-04-13", emoji: "🟠", cat: "hipotesis", evento: "José Samamé es detenido · iPhone incautado", fuente: "Prensa (pendiente verificar)" },
  { fecha: "2026-04-23", emoji: "🟢", cat: "probado", evento: "Pleno del JNE declara INVIABLES las elecciones complementarias", fuente: "Acuerdo del Pleno JNE 23-abr-2026" },
  { fecha: "2026-04-24", emoji: "🟠", cat: "hipotesis", evento: "12 allanamientos Fiscalía Anticorrupción + DIRCOCOR · 5:40 a.m.", fuente: "Prensa (pendiente verificar)" },
  { fecha: "2026-04-27", emoji: "🟠", cat: "hipotesis", evento: "Revelación: iPhone de Samamé reactivado durante allanamiento", fuente: "Prensa (pendiente verificar)" },
  { fecha: "2026-05-11", emoji: "🟠", cat: "hipotesis", evento: "Fiscalía reporta 70+ denuncias · 18 meses impedimento salida", fuente: "Prensa (pendiente verificar)" },
  { fecha: "2026-05-22", emoji: "🟢", cat: "probado", evento: "CGR emite Informe de Control 8536-2026 (Tomo I de XIV) · halla responsabilidad ADMINISTRATIVA FUNCIONAL + PENAL contra 8 funcionarios ONPE · recomienda inicio de acciones penales por la Procuraduría Pública Anticorrupción + conocimiento a la JNJ", fuente: "CGR 8536-2026 PDF directo" },
];

const RECOMENDACIONES_CGR = [
  { destino: "Titular de ONPE", accion: "Realizar acciones tendentes a que el órgano competente efectúe el deslinde de responsabilidades administrativo-funcionales de los funcionarios y servidores comprendidos", conclusiones: "1, 2, 3" },
  { destino: "Procuraduría Pública Especializada en Delitos de Corrupción de Funcionarios", accion: "Iniciar las ACCIONES PENALES contra los funcionarios y servidores comprendidos para determinar responsabilidades", conclusiones: "1, 2, 3" },
  { destino: "Junta Nacional de Justicia (JNJ)", accion: "Poner en conocimiento el presente Informe a fin de que disponga el inicio de las acciones que correspondan", conclusiones: "2" },
];

const FUENTES = [
  { titulo: "Informe de Control 8536-2026-CG/JUSPE-SCE (PDF · Tomo I · 5.6 MB)", url: "/cgr/informe_8536_2026_tomo_I.pdf", tipo: "PRIMARIA" },
  { titulo: "CGR — Subgerencia de Control del Sector Justicia, Político y Electoral", url: "https://www.contraloria.gob.pe/", tipo: "INSTITUCIONAL" },
  { titulo: "Acuerdo del Pleno JNE 23-abr-2026 (pendiente subir PDF primario)", url: "https://busquedas.elperuano.pe/", tipo: "SECUNDARIA" },
  { titulo: "Allanamiento Corvetto · El Comercio (pendiente verificar URL exacta)", url: "https://elcomercio.pe/politica/piero-corvetto-allanamiento-argumentos", tipo: "PRENSA" },
  { titulo: "ONPE ignoró alerta corrupción · Infobae (pendiente verificar)", url: "https://www.infobae.com/peru/2026/04/27/onpe-piero-corvetto-recibio-alerta", tipo: "PRENSA" },
];

const chipFor = (cat: string): string =>
  ({ probado: "chip-probado", contradiccion: "chip-contradiccion", hipotesis: "chip-hipotesis" }[cat] ?? "chip-vacio");

const fmtSoles = (n: number) =>
  new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN", maximumFractionDigits: 2 }).format(n);

const fmtVotos = (n: number) => new Intl.NumberFormat("es-PE").format(n);

export function CasoGalagaPage() {
  return (
    <div className="container-prose py-12 md:py-16">
      <div className="flex flex-wrap items-center gap-2">
        <span className="chip-probado">🟢 Responsabilidad formalizada por Contraloría</span>
        <span className="chip-contradiccion">🔴 Recomienda acciones penales</span>
        <span className="chip-reconocido">🔵 Cifras ratificadas por JNE Pleno 23-abr</span>
      </div>

      <h1 className="mt-6 font-serif text-4xl text-ink-50 md:text-5xl">Caso Gálaga</h1>
      <p className="mt-3 text-xs uppercase tracking-widest text-gold-400/80">
        Contrato logístico ONPE EG2026 · Informe CGR 8536-2026 (22-may-2026)
      </p>
      <p className="mt-4 max-w-3xl text-lg text-ink-200">
        El 22 de mayo de 2026 la Contraloría General de la República emitió el{" "}
        <strong className="text-ink-50">Informe de Control 8536-2026-CG/JUSPE-SCE</strong> (Tomo I
        de XIV) sobre la contratación del servicio de transporte y distribución del material y
        equipos informáticos electorales en Lima Metropolitana y Callao para las EG2026. Halla
        responsabilidad administrativa funcional y penal contra ocho funcionarios y servidores de
        la ONPE, y recomienda formalmente <em>iniciar acciones penales</em> a través de la
        Procuraduría Pública Anticorrupción.
      </p>

      <BannerFuentes pagina="/caso-galaga" />

      {/* CGR · destacado primario */}
      <section className="mt-10 rounded-lg border border-gold-500/40 bg-gold-500/5 p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="chip-probado">🟢 Fuente primaria</span>
            <span className="text-xs uppercase tracking-widest text-gold-400/80">
              Contraloría General de la República
            </span>
          </div>
          <a
            href="/cgr/informe_8536_2026_tomo_I.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-md bg-gold-500/20 px-3 py-1.5 text-xs text-gold-200 hover:bg-gold-500/30"
          >
            Descargar Informe completo (PDF · 5.6 MB) →
          </a>
        </div>
        <h2 className="mt-3 font-serif text-2xl text-ink-50">
          Informe de Control 8536-2026-CG/JUSPE-SCE
        </h2>
        <p className="mt-1 text-sm text-ink-200">
          Subgerencia de Control del Sector Justicia, Político y Electoral · Período auditado: 6
          de febrero de 2025 al 13 de abril de 2026 · Tomo I de XIV · 130 páginas.
        </p>
        <p className="mt-3 text-sm text-ink-200">
          <strong className="text-ink-50">Hechos imputados:</strong> Tres irregularidades:
          (1) términos de referencia mal elaborados y evaluación amañada que favoreció a Gálaga
          pese a tener oferta más cara, (2) traslado de equipos para capacitación con
          reprogramación de rutas a 24h del día D, (3) prestaciones adicionales por S/ 650,205.84
          sin sustento técnico.
        </p>
      </section>

      {/* Contrato resumen */}
      <section className="mt-10">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">El contrato y las ofertas competidoras</h2>
        <div className="mt-3 grid gap-3 md:grid-cols-4">
          <KpiCard label="Monto adjudicado a Gálaga" value={fmtSoles(EXPEDIENTE.contratoPrincipal.monto)} hint={EXPEDIENTE.contratoPrincipal.numero} />
          <KpiCard label="Prestaciones adicionales" value={fmtSoles(EXPEDIENTE.prestacionesAdicionales.monto)} hint="sin sustento técnico (CGR)" />
          <KpiCard label="Vehículos propios Gálaga" value={`${EXPEDIENTE.vehiculosUsados.propios} / ${EXPEDIENTE.vehiculosUsados.total} (8%)`} hint={`${EXPEDIENTE.vehiculosUsados.terceros} fueron de terceros (92%)`} />
          <KpiCard label="Incremento precio repliegue" value="+600%" hint="S/ 0.50 → S/ 6.00 por kg" />
        </div>
        <div className="mt-3 overflow-x-auto rounded-lg border border-ink-700/60 bg-ink-900/50">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-700/60 text-left text-xs uppercase tracking-wide text-ink-300">
                <th className="px-3 py-3">Postor</th>
                <th className="px-3 text-right">Oferta económica</th>
                <th className="px-3 text-right">Δ vs Gálaga</th>
                <th className="px-3">Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-ink-700/30 bg-sem-contradiccion/5">
                <td className="px-3 py-2 text-ink-100">Servicios Generales Gálaga S.A.C.</td>
                <td className="px-3 text-right font-mono tabular-nums text-ink-100">{fmtSoles(EXPEDIENTE.contratoPrincipal.monto)}</td>
                <td className="px-3 text-right text-ink-400">—</td>
                <td className="px-3 text-sem-contradiccion text-xs">🔴 ADJUDICADA (puntaje indebido según CGR)</td>
              </tr>
              {EXPEDIENTE.ofertasCompetidoras.map((o) => (
                <tr key={o.empresa} className="border-b border-ink-700/30">
                  <td className="px-3 py-2 text-ink-200">{o.empresa}</td>
                  <td className="px-3 text-right font-mono tabular-nums text-ink-200">{fmtSoles(o.monto)}</td>
                  <td className="px-3 text-right font-mono tabular-nums text-sem-probado">{fmtSoles(o.deltaConGalaga)}</td>
                  <td className="px-3 text-ink-400 text-xs">No adjudicada</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-ink-400">
          La empresa Senlas presentó la oferta más barata <strong className="text-sem-probado">por S/ 469,404.09 menos</strong>{" "}
          que Gálaga. CGR halla que los evaluadores aplicaron criterios no previstos en TdR (reducir 2 m al largo de los vehículos)
          y consignaron datos inexactos sobre capacidad de carga del postor adjudicado.
        </p>
      </section>

      {/* Universo afectado · cifras CGR↔JNE byte-exactas */}
      <section className="mt-10">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">
          Universo afectado por el fallo logístico
        </h2>
        <p className="mt-1 text-xs text-ink-400">
          Cifras ratificadas por CGR (Informe 8536-2026) y JNE (Acuerdo Pleno 23-abr-2026)
        </p>
        <div className="mt-3 grid gap-3 md:grid-cols-4">
          <KpiCard label="Locales con material retrasado" value={fmtVotos(256)} />
          <KpiCard label="Mesas afectadas" value={fmtVotos(3_605)} hint="distribución inoportuna" />
          <KpiCard label="Electores en universo" value={fmtVotos(1_071_111)} hint="en los 256 locales" />
          <KpiCard label="Electores sin votar" value={fmtVotos(55_261)} hint="187 mesas no instaladas en 13 locales Lima Metro" highlight />
        </div>
      </section>

      {/* Investigados CGR */}
      <section className="mt-10">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">
          Funcionarios identificados por CGR ({INVESTIGADOS_CGR.length})
        </h2>
        <p className="mt-1 text-xs text-ink-400">
          Capítulo IV "Identificación de personas involucradas en los hechos específicos
          presuntamente irregulares"
        </p>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {INVESTIGADOS_CGR.map((inv) => (
            <div key={inv.dni} className="rounded-lg border border-ink-700/60 bg-ink-900/40 p-4">
              <div className="font-serif text-ink-50 text-lg">{inv.nombre}</div>
              <div className="mt-0.5 text-xs text-ink-400">
                {inv.cargo} · <span className="font-mono">DNI {inv.dni}</span>
              </div>
              <div className="mt-1 text-xs text-ink-400">Periodo: {inv.periodo}</div>
              <ul className="mt-2 space-y-1 text-sm text-ink-200">
                {inv.hechos.map((h, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-sem-contradiccion">•</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <h3 className="mt-6 text-xs uppercase tracking-widest text-ink-400">
          Investigados por otra vía (Fiscalía · no en informe CGR)
        </h3>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {INVESTIGADOS_OTROS.map((inv) => (
            <div key={inv.nombre} className="rounded-lg border border-ink-700/40 bg-ink-900/30 p-4">
              <div className="font-serif text-ink-100">{inv.nombre}</div>
              <div className="mt-0.5 text-xs text-ink-400">{inv.cargo}</div>
              <ul className="mt-2 space-y-1 text-sm text-ink-200">
                {inv.hechos.map((h, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-ink-400">•</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Recomendaciones CGR */}
      <section className="mt-10 rounded-lg border border-sem-contradiccion/40 bg-sem-contradiccion/5 p-5">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">
          Recomendaciones formales de la Contraloría
        </h2>
        <div className="mt-3 space-y-3">
          {RECOMENDACIONES_CGR.map((r, i) => (
            <div key={i} className="rounded-md border border-ink-700/40 bg-ink-900/40 p-3">
              <div className="text-xs uppercase tracking-widest text-gold-400/80">
                Recomendación {i + 1} → {r.destino}
              </div>
              <p className="mt-1 text-sm text-ink-100">{r.accion}</p>
              <div className="mt-1 text-xs text-ink-400">Aplica a conclusiones: {r.conclusiones}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section className="mt-10">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">Línea de tiempo</h2>
        <ol className="mt-4 space-y-3">
          {TIMELINE.map((h, i) => (
            <li key={i} className="rounded-lg border border-ink-700/60 bg-ink-900/40 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className={chipFor(h.cat)}>
                  {h.emoji} {h.cat.replace("contradiccion", "contradicción")}
                </span>
                <time className="text-xs text-ink-400 font-mono">{h.fecha}</time>
              </div>
              <p className="mt-2 text-ink-100">{h.evento}</p>
              {h.fuente && <p className="mt-1 text-xs text-ink-400">Fuente: {h.fuente}</p>}
            </li>
          ))}
        </ol>
      </section>

      {/* Fuentes */}
      <section className="mt-10 rounded-lg border border-ink-700/60 bg-ink-900/30 p-5">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">Fuentes</h2>
        <ul className="mt-3 space-y-1.5 text-sm">
          {FUENTES.map((f) => (
            <li key={f.url}>
              <span
                className={`mr-2 inline-block rounded px-1.5 py-0.5 text-xs font-mono ${
                  f.tipo === "PRIMARIA"
                    ? "bg-sem-probado/20 text-sem-probado"
                    : f.tipo === "INSTITUCIONAL"
                    ? "bg-gold-500/15 text-gold-300"
                    : f.tipo === "SECUNDARIA"
                    ? "bg-ink-700 text-ink-200"
                    : "bg-ink-800 text-ink-400"
                }`}
              >
                {f.tipo}
              </span>
              <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-gold-300 hover:underline">
                {f.titulo} ↗
              </a>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function KpiCard({ label, value, hint, highlight }: { label: string; value: string; hint?: ReactNode; highlight?: boolean }) {
  return (
    <div className={`rounded-lg border p-4 ${highlight ? "border-sem-contradiccion/40 bg-sem-contradiccion/5" : "border-ink-700/60 bg-ink-900/60"}`}>
      <div className="text-xs uppercase tracking-widest text-ink-400">{label}</div>
      <div className={`mt-1 font-mono text-xl tabular-nums ${highlight ? "text-sem-contradiccion" : "text-ink-50"}`}>{value}</div>
      {hint && <div className="mt-1 text-xs text-ink-400">{hint}</div>}
    </div>
  );
}
